<template>
  <section class="responsibility-assignments-tab">
    <ResponsibilityAssignmentsTable
      :current-response="currentResponse"
      :data-stream="assignmentsListData"
      :responsibility-id="responsibilityId"
      :account-id="accountId"
      :pagination-config="pagination"
      :loading="assignmentsLoading"
      @weightChanged="handleWeightChanged"
      @badgeCountsUpdated="onBadgeCountsUpdated"
      :page-type="pageType"
      :hide-badge-remove-icon="hideBadgeRemoveIcon"
      :expiration-icon-read-only="expirationIconReadOnly"
    />
  </section>
</template>

<script setup>
import { ref, computed, watch, onMounted, getCurrentInstance } from "vue";
import ResponsibilityAssignmentsTable from "@components/ResponsibilityAssignmentsTable/ResponsibilityAssignmentsTable.vue";
import { getResponsibilityAssignments } from "@/api";

const instance = getCurrentInstance();
const proxy = instance?.proxy;

const props = defineProps({
  currentResponse: {
    type: Object,
    default: () => ({}),
  },
  dataStream: {
    type: Array,
    default: () => [],
  },
  responsibilityId: {
    type: [Number, String],
    default: null,
  },
  accountId: {
    type: [Number, String],
    default: null,
  },
  pageType: {
    type: String,
    default: "responsibility",
  },
  pagination: {
    type: Object,
    default: null,
  },
  hideBadgeRemoveIcon: {
    type: Boolean,
    default: false,
  },
  expirationIconReadOnly: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["weight-changed", "updateAssignmentsCount", "badgeCountsUpdated"]);

const assignmentsList = ref([]);
const assignmentsLoading = ref(false);

const assignmentsListData = computed(() => {
  if (props.dataStream && props.dataStream.length > 0) {
    return props.dataStream;
  }
  return assignmentsList.value;
});

function handleWeightChanged(data) {
  emit("weight-changed", data);
}

function onBadgeCountsUpdated() {
  emit("badgeCountsUpdated");
}

function formattedDate(dateString) {
  if (!dateString) return null;

  const dateObj = new Date(dateString);
  if (isNaN(dateObj.getTime())) return null;

  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const day = String(dateObj.getDate()).padStart(2, "0");
  const month = months[dateObj.getMonth()];
  const year = dateObj.getFullYear();
  const hours = String(dateObj.getHours()).padStart(2, "0");
  const minutes = String(dateObj.getMinutes()).padStart(2, "0");

  return `${day}-${month}-${year} ${hours}:${minutes}`;
}

function getAssignmentStatus(status) {
  if (status?.includes("Terminated")) {
    return "terminated";
  } else if (status?.includes("Retired")) {
    return "retired";
  } else if (status?.includes("Relieved")) {
    return "relieved";
  } else if (status?.includes("Detached")) {
    return "detached";
  }
  return status;
}

async function getRespAssignments() {
  const respId = props.responsibilityId || props.currentResponse?.id;
  const accId = props.accountId || JSON.parse(localStorage.getItem("currentAccountID"));

  if (!respId || !accId) {
    console.warn("Cannot fetch assignments: Missing responsibility ID or account ID");
    return;
  }

  try {
    proxy?.$loader?.show();
    assignmentsLoading.value = true;
    const { data } = await getResponsibilityAssignments(accId, respId);

    if (data && Array.isArray(data)) {
      const respAssignments = data.map((el) => {
        const assignedDate = formattedDate(el.assigned_on);
        const removedDate = formattedDate(el.removed_on);
        let employeeName = el.employee_name?.split(" ") || [];
        const regex = /(#\d+)\s*-\s*(.*)/;
        const match = el.position_code?.match(regex);
        const code = match?.[1];
        const position = el.position_name;
        const roleMatch = el.role_code?.match(regex);
        const roleCode = roleMatch?.[1];
        const roleName = el.role_name;

        const roleId = el.role_id ?? el.role?.id ?? (typeof el.role === "number" ? el.role : null);
        const newEl = {
          id: el.id,
          avatar: el.employee_avatar,
          nameUrl: `/organization/employees/${el.employee_id}/pathtoreadiness`,
          first_name: employeeName[0] || "",
          last_name: employeeName.slice(1).join(" ") || "",
          title: el.employee_title || "",
          availability: el.employee_availability,
          current_status: getAssignmentStatus(el.status),
          badge_id: el.badge_id,
          assignment_id: el.assignment_id,
          assigned_by: el.assigned_by,
          assigned_by_avatar: el.assigned_by_avatar,
          assigned_on: assignedDate,
          removed_by: el.removed_by,
          removed_by_avatar: el.removed_by_avatar,
          removed_on: removedDate,
          importance_level: el.importance,
          position_name: position,
          position_code: code,
          positionNameCode: el.position_code,
          roleNameCode: el.role_code,
          role_name: roleName,
          role_code: roleCode,
          role_id: roleId,
        };
        return newEl;
      });

      assignmentsList.value = respAssignments;

      emit("updateAssignmentsCount", respAssignments.length);
    }
  } catch (error) {
    console.error("Error fetching responsibility assignments:", error);
    assignmentsList.value = [];
  } finally {
    proxy?.$loader?.hide();
    assignmentsLoading.value = false;
  }
}

watch(
  () => [props.responsibilityId, props.currentResponse?.id],
  ([newRespId, newCurrentRespId]) => {
    const respId = newRespId || newCurrentRespId;
    if (respId) {
      getRespAssignments();
    }
  },
  { immediate: false }
);

onMounted(() => {
  if (!props.dataStream || props.dataStream.length === 0) {
    const respId = props.responsibilityId || props.currentResponse?.id;
    if (respId) {
      getRespAssignments();
    }
  }
});

defineExpose({
  refreshAssignments: getRespAssignments,
});
</script>

<style scoped>
.responsibility-assignments-tab {
  width: 100%;
  display: flex;
  flex-direction: column;
}
</style>
